import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { client, AUTH_USER, CREATE_CHAT, UPDATE_CHAT, filterChat, REMOVE_CHAT } from '../../graphqlClient/graphql';
import { useMutation, useQuery } from '@apollo/client';

interface Message {
    senderId: string;
    text: string;
    createdAt: Date;
}

interface Chat {
    _id: string;
    senderId: any; // User object
    receiverId: any; // User object
    messages: Message[];
    updatedAt: Date;
}

interface ChatContextType {
    socket: Socket | null;
    activeChats: Chat[];
    currentChat: Chat | null;
    setCurrentChat: (chat: Chat | null) => void;
    sendMessage: (text: string) => void;
    startChat: (receiverId: string) => Promise<void>;
    deleteChat: (chatId: string) => Promise<void>;
    isChatOpen: boolean;
    setIsChatOpen: (isOpen: boolean) => void;
    refreshChats: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [activeChats, setActiveChats] = useState<Chat[]>([]);
    const [currentChat, setCurrentChat] = useState<Chat | null>(null);
    const [isChatOpen, setIsChatOpen] = useState(false);

    // Use ref to track current chat in socket event handlers
    const currentChatRef = useRef<Chat | null>(null);

    const user = useSelector((state: any) => state.user.user);

    const [createChat] = useMutation(CREATE_CHAT);
    const [removeChat] = useMutation(REMOVE_CHAT);

    // Update ref whenever currentChat changes
    useEffect(() => {
        currentChatRef.current = currentChat;
    }, [currentChat]);

    useEffect(() => {
        if (user?._id) {
            const newSocket = io('https://api.wescout.me', {
                query: { userId: user._id },
                autoConnect: true
            });

            newSocket.on('connect', () => {
                //console.log('Socket connected');
                newSocket.emit('identity', user._id);
            });

            newSocket.on('receiveMessage', (data: any) => {
                //console.log('Received message:', data);

                // Add message to current chat if it's the active one
                const activeChat = currentChatRef.current;
                if (activeChat && activeChat._id === data.chatId) {
                    const newMessage: Message = {
                        senderId: data.senderId,
                        text: data.content,
                        createdAt: new Date()
                    };

                    const updatedChat = {
                        ...activeChat,
                        messages: [...activeChat.messages, newMessage]
                    };
                    setCurrentChat(updatedChat);

                    // Also update in activeChats list
                    setActiveChats(prev => prev.map(c =>
                        c._id === data.chatId ? updatedChat : c
                    ));
                } else {
                    // If not current chat, just refresh to update the list
                    refreshChats();
                }
            });

            setSocket(newSocket);
            refreshChats();

            return () => {
                newSocket.disconnect();
            };
        }
    }, [user]);

    const refreshChats = async () => {
        if (user?._id) {
            // Fetch chats where user is sender OR receiver
            const senderChats = await filterChat({ senderId: user._id });
            const receiverChats = await filterChat({ receiverId: user._id });

            // Merge and deduplicate (simple approach)
            const allChats = [...(senderChats || []), ...(receiverChats || [])];
            const uniqueChats = Array.from(new Map(allChats.map(item => [item._id, item])).values());

            // Sort by updated at
            uniqueChats.sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

            setActiveChats(uniqueChats as Chat[]);

            // Update current chat if it exists
            if (currentChat) {
                const updatedCurrent = uniqueChats.find((c: any) => c._id === currentChat._id);
                if (updatedCurrent) setCurrentChat(updatedCurrent as Chat);
            }
        }
    };

    const sendMessage = (text: string) => {
        if (socket && currentChat && user?._id) {
            const messageData = {
                senderId: user._id,
                receiverId: currentChat.receiverId._id === user._id ? currentChat.senderId._id : currentChat.receiverId._id,
                content: text,
                chatId: currentChat._id
            };

            socket.emit('sendMessage', messageData);

            // Optimistically update UI
            const newMessage: Message = {
                senderId: user._id,
                text: text,
                createdAt: new Date()
            };

            const updatedChat = { ...currentChat, messages: [...currentChat.messages, newMessage] };
            setCurrentChat(updatedChat);

            // Also update in activeChats list
            setActiveChats(prev => prev.map(c => c._id === currentChat._id ? updatedChat : c));
        }
    };

    const startChat = async (receiverId: string) => {
        try {
            // Check if chat already exists
            const existingChat = activeChats.find(c =>
                (c.senderId._id === user._id && c.receiverId._id === receiverId) ||
                (c.senderId._id === receiverId && c.receiverId._id === user._id)
            );

            if (existingChat) {
                setCurrentChat(existingChat);
                setIsChatOpen(true);
                return;
            }

            // Create new chat
            const { data } = await createChat({
                variables: {
                    createChatInput: {
                        senderId: user._id,
                        receiverId: receiverId,
                        messages: [],
                        type: 'user'
                    }
                }
            });

            if (data?.createChat) {
                setCurrentChat(data.createChat);
                setIsChatOpen(true);
                refreshChats();
            }
        } catch (error) {
            console.error("Error starting chat:", error);
        }
    };

    const deleteChat = async (chatId: string) => {
        try {
            await removeChat({ variables: { id: chatId } });
            refreshChats();
            if (currentChat?._id === chatId) {
                setCurrentChat(null);
                setIsChatOpen(false);
            }
        } catch (error) {
            console.error("Error deleting chat:", error);
        }
    };

    return (
        <ChatContext.Provider value={{
            socket,
            activeChats,
            currentChat,
            setCurrentChat,
            sendMessage,
            startChat,
            deleteChat,
            isChatOpen,
            setIsChatOpen,
            refreshChats
        }}>
            {children}
        </ChatContext.Provider>
    );
};
